import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faWarning } from '@fortawesome/pro-regular-svg-icons';
import type { ReactNode } from 'react';
import { FormattedMessage, FormattedNumber, defineMessages } from 'react-intl';
import styled from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { LoadingButton } from '@sorare/core/src/atoms/buttons/LoadingButton';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Vault } from '@sorare/core/src/atoms/icons/Vault';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import {
  BodyM,
  HeadlineL,
  LabelM,
  LabelS,
} from '@sorare/core/src/atoms/typography';
import { proxyUrl } from '@sorare/core/src/atoms/ui/ResponsiveImg';
import { SplitProgressBar } from '@sorare/core/src/atoms/ui/SplitProgressBar';
import { Dialog } from '@sorare/core/src/components/dialog';
import { CardsCarousel } from '@sorare/core/src/components/landing/CollectCards/CardsCarousel';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { range } from '@sorare/core/src/lib/arrays';
import { glossary } from '@sorare/core/src/lib/glossary';

import { RarityVaultBannerWithFetch } from '#src/components/sealing/RarityVaultBanner/index.withFetch.tsx';
import bg from '#src/components/sealing/assets/dialog-bg.webp';
import { TokenTransferValidator } from '#src/components/token/TokenTransferValidator/index.tsx';

import type { UnsealCardDialog_anyCard } from './__generated__/index.graphql.ts';

const Root = styled(Dialog)`
  &,
  &.fullscreen {
    background: url(${bg}) no-repeat center center;
    background-size: cover;
    border-radius: var(--double-unit) !important;
  }
`;

const InnerDialog = styled(Vertical).attrs({ center: true, gap: 2 })`
  padding: 0 var(--quadruple-unit) var(--quadruple-unit);
`;
const CardsContainer = styled.div`
  padding: 0 var(--quadruple-unit);
  margin: 0 auto;
`;
const Content = styled(Vertical).attrs({ gap: 3 })`
  width: 100%;
`;

const WarningMessage = styled.div`
  margin: var(--double-unit) var(--quadruple-unit);
`;

const Actions = styled(Horizontal).attrs({ gap: 1.5 })`
  padding: 0 var(--triple-unit) var(--triple-unit);
  margin-top: var(--double-and-a-half-unit);
`;
const NoWrap = styled.span`
  white-space: nowrap;
`;

const BarContainer = styled(Vertical).attrs({ gap: 0.5 })`
  width: 100%;
`;

const BarGrid = styled.div`
  display: grid;
  grid-template-columns: 2em 1fr 2em;
  align-items: center;
  gap: var(--unit);
`;

const StyledLabelM = styled(LabelM).attrs({
  bold: true,
  uppercase: true,
})`
  font-family: var(--brand-font);
`;
const StyledLabelS = styled(LabelS).attrs({
  bold: true,
  uppercase: true,
})`
  font-family: var(--brand-font);
`;

type Props = {
  open: boolean;
  rarity?: string;
  card?: UnsealCardDialog_anyCard;
  onClose: () => void;
  onConfirm: () => void;
  loading: boolean;
  unsealing: boolean;
};

const subtitleBySport = defineMessages({
  [Sport.BASEBALL]: {
    id: 'CardsSealingSummary.unsealDialog.explanation',
    defaultMessage: 'Your Vault & Loyalty levels may decrease.',
  },
  [Sport.NBA]: {
    id: 'CardsSealingSummary.unsealDialog.explanation',
    defaultMessage: 'Your Vault & Loyalty levels may decrease.',
  },
  [Sport.FOOTBALL]: {
    id: 'CardsSealingSummary.unsealDialog.footballExplanation',
    defaultMessage:
      'Your Vault level will decrease and may lose reward bonuses.',
  },
});

export const UnsealCardDialog = ({
  open = true,
  card,
  onClose,
  onConfirm,
  loading,
  unsealing,
}: Props) => {
  const { sport } = useSportContext();
  if (!card) {
    return null;
  }
  const cards = card ? [card] : [];

  const { playerLoyaltyBoost, unsealedPlayerLoyaltyBoost } = card;
  const maximumPoints = playerLoyaltyBoost.maximumBoostLevel?.points ?? 1;
  return (
    <Root
      maxWidth="xs"
      fullWidth
      open={open}
      onClose={onClose}
      footer={
        open ? (
          <TokenTransferValidator cards={cards} transferContext="unseal">
            {({ validationMessages }) => (
              <div>
                {Object.values(validationMessages).map(
                  (warningMessage, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <WarningMessage key={index}>
                      <Vertical>{warningMessage}</Vertical>
                    </WarningMessage>
                  )
                )}

                <Actions>
                  <Button
                    disabled={loading || unsealing}
                    size="medium"
                    onClick={onClose}
                    color="quaternary"
                    fullWidth
                  >
                    <FormattedMessage {...glossary.cancel} />
                  </Button>
                  <LoadingButton
                    loading={loading || unsealing}
                    size="medium"
                    color="red"
                    onClick={onConfirm}
                    fullWidth
                    startIcon={
                      Object.keys(validationMessages).length > 0 ? (
                        <FontAwesomeIcon icon={faWarning} />
                      ) : (
                        <Vault size={2} action="unvault" />
                      )
                    }
                  >
                    <FormattedMessage
                      id="CardsSealingSummary.unsealCTA"
                      defaultMessage="Unseal"
                    />
                  </LoadingButton>
                </Actions>
              </div>
            )}
          </TokenTransferValidator>
        ) : null
      }
    >
      {!loading && (
        <InnerDialog className="text-center">
          <Content>
            <Vertical>
              <HeadlineL color="var(--c-white)">
                <FormattedMessage
                  id="CardsSealingSummary.unsealDialog.title"
                  defaultMessage="Are you sure?"
                  values={{
                    cardsCount: cards.length,
                    nowrap: (chunks: ReactNode[]) => {
                      return <NoWrap>{chunks}</NoWrap>;
                    },
                  }}
                />
              </HeadlineL>
              <BodyM color="var(--c-nd-600)">
                <FormattedMessage {...subtitleBySport[card.sport]} />
              </BodyM>
            </Vertical>
            <CardsContainer>
              <CardsCarousel
                cards={cards.map(c => ({
                  src: proxyUrl(c.pictureUrl || '', {
                    cropWidth: 160,
                  }),
                  alt: `${c.anyPlayer?.displayName}${c.rarityTyped ? ` - ${c.rarityTyped}` : ''}`,
                }))}
                slotSize={160}
              />
            </CardsContainer>
            {sport === Sport.BASEBALL ? (
              <BarContainer gap={0.5}>
                <Horizontal center>
                  <StyledLabelS color="var(--c-nd-600)">
                    <FormattedMessage
                      id="TokenLoyaltyBoost.playerLoyalty"
                      defaultMessage="Player Loyalty"
                    />
                  </StyledLabelS>
                  <StyledLabelM color="var(--c-loyalty)">-1</StyledLabelM>
                </Horizontal>
                <BarGrid>
                  <StyledLabelM color="var(--c-nd-900)">
                    {unsealedPlayerLoyaltyBoost.points}/{maximumPoints}
                  </StyledLabelM>
                  <div>
                    <SplitProgressBar
                      thresholds={range(maximumPoints).map(i => ({
                        score: i + 1,
                      }))}
                      currentScore={unsealedPlayerLoyaltyBoost.points}
                      color="var(--c-loyalty)"
                      variant="oblique"
                    />
                  </div>
                  <StyledLabelM color="var(--c-nd-500)">
                    <FormattedNumber
                      value={
                        (unsealedPlayerLoyaltyBoost.nextBoostLevel
                          ?.boostBasisPoints || 0) /
                        100 /
                        100
                      }
                      style="percent"
                      signDisplay="always"
                    />
                  </StyledLabelM>
                </BarGrid>
              </BarContainer>
            ) : (
              <RarityVaultBannerWithFetch
                rarity={card.rarityTyped}
                additionalPoints={-1 * (card.sealedFor || 0)}
                withBackground={false}
                showHowItWorksCta={false}
              />
            )}
          </Content>
        </InnerDialog>
      )}
    </Root>
  );
};

UnsealCardDialog.fragments = {
  anyCard: gql`
    fragment UnsealCardDialog_anyCard on AnyCardInterface {
      slug
      rarityTyped
      pictureUrl
      sealedFor
      unsealedPlayerLoyaltyBoost {
        points
        boostLevel {
          boostBasisPoints
        }
        nextBoostLevel {
          points
          boostBasisPoints
        }
      }
      playerLoyaltyBoost {
        points
        maximumBoostLevel {
          points
        }
      }
      anyPlayer {
        slug
        displayName
      }
      ...TokenTransferValidator_anyCard
    }
    ${TokenTransferValidator.fragments.anyCard}
  ` as TypedDocumentNode<UnsealCardDialog_anyCard>,
};
